const Settings = {
    videoEncoderCodecs: [
        'hvc1.1.6.L93.90', // H.265/HEVC codec (widely supported)
        'avc1.4d0033', // H.264/AVC codec (widely supported)
        'avc1.420034',
    ],
    videoEncoderBitrateSd: 250_000, // broadcaster upload bw requirement (4x) 1Mbit/s
    videoEncoderDefaultFrameRate: 30,
    videoEncoderDefaultWidth: 1280,
    videoEncoderDefaultHeight: 720};

/**
 * Attempts to detect a supported `VideoEncoderConfig` by testing a set of codecs and parameters.
 *
 * The function iterates over the provided codec list (or defaults from `Settings`), adjusting
 * video width and height to be even numbers (required by some encoders). For each codec, it
 * calls `VideoEncoder.isConfigSupported` to determine if the configuration is supported in
 * the current browser environment.
 *
 * @param options - Partial configuration object to override default encoder settings.
 * @returns A Promise resolving to a valid `VideoEncoderConfig` if one is found, otherwise `null`.
 *
 * @example
 * ```ts
 * const config = await detectVideoEncoderConfig({
 *   bitrate: 2_000_000,
 *   codecs: ['vp9', 'avc1.42E01E'],
 *   frameRate: 30
 * });
 *
 * if (config) {
 *   console.log("Using config:", config);
 * } else {
 *   console.error("No supported config found.");
 * }
 * ```
 */
async function tryCodecs(codecs, baseConfig, annexb) {
    for (const codec of codecs) {
        const config = { ...baseConfig, codec };
        if (annexb) {
            if (codec.startsWith('hvc1') || codec.startsWith('hev1')) {
                config.hevc = {
                    format: 'annexb',
                };
            }
            else if (codec.startsWith('avc1')) {
                config.avc = {
                    format: 'annexb',
                };
            }
        }
        try {
            const codecSupport = await VideoEncoder.isConfigSupported(config);
            if (codecSupport.supported) {
                console.log('Found supported codec:', codec, `(${config.hardwareAcceleration})`);
                return config;
            }
            console.log('This browser does not support codec:', codec);
        }
        catch (error) {
            console.warn(`Error checking support for codec ${codec} with config:`, config, error);
        }
    }
    return null;
}
async function detectVideoEncoderConfig(options = {}) {
    const { annexb = false, bitrate = Settings.videoEncoderBitrateSd, codecs = Settings.videoEncoderCodecs, frameRate = Settings.videoEncoderDefaultFrameRate, hardwareAcceleration = 'prefer-hardware', height: heightParam = Settings.videoEncoderDefaultHeight, latencyMode = 'quality', width: widthParam = Settings.videoEncoderDefaultWidth, } = options;
    const width = widthParam - (widthParam % 2);
    const height = heightParam - (heightParam % 2);
    const baseConfig = {
        bitrate,
        framerate: frameRate,
        hardwareAcceleration,
        height,
        latencyMode,
        width,
    };
    const result = await tryCodecs(codecs, baseConfig, annexb);
    if (result)
        return result;
    if (hardwareAcceleration === 'prefer-hardware') {
        console.log('No codec supported with prefer-hardware, retrying with no-preference');
        const fallbackResult = await tryCodecs(codecs, { ...baseConfig, hardwareAcceleration: 'no-preference' }, annexb);
        if (fallbackResult)
            return fallbackResult;
    }
    console.error('Could not find supported codec configuration. Tried:', codecs.join(', '));
    return null;
}

export { detectVideoEncoderConfig };
